/**
 * Which authoring pipeline's shelves the reader is looking at.
 *
 * A VIEW rather than a chip, and the difference is why it is drawn up
 * beside the crumbs instead of down among the filters.  The server
 * narrows to the pipeline BEFORE it counts the facets, so every count
 * on the page — every level, every vessel, every review state — reads
 * as "how many of what this pipeline made".  That is what turns "is v2
 * better?" into something a reviewer can see at a glance rather than
 * arithmetic they have to do.
 *
 * It also survives walking the tree, because "show me what v2 made" is
 * a question carried from ring to planet, not one re-asked at every
 * click.
 *
 * The same control serves the publish modal, where it narrows the file
 * list.  One control, one idiom, one place to change it: every pipeline
 * draws its own `ring_1`, so an operator who cannot separate them is
 * reading two shelves stacked on top of each other.
 */

/** `pipeline_v2` → `v2`.  The prefix is the same on every one of them,
 *  so showing it costs width and says nothing. */
export function shortPipeline(name: string): string {
  return name.startsWith("pipeline_") ? name.slice("pipeline_".length) : name;
}

export function PipelineView({
  value,
  pipelines,
  counts,
  onChange,
  label = "forged by",
  testId = "library-pipeline-view",
}: {
  value: string;
  /** Every pipeline that EXISTS, from the server's vocabulary — not the
   *  set present in the rows.  An empty shelf is a real answer, and a
   *  control built from the data alone could never say it. */
  pipelines: string[] | undefined;
  /** How many rows each holds in the current scope, when that is known. */
  counts?: Record<string, number>;
  onChange: (pipeline: string) => void;
  label?: string;
  testId?: string;
}) {
  const known = pipelines ?? [];
  // One pipeline is the normal case for a long time, and a switch with
  // one position is furniture.  It appears when there is a choice.
  if (known.length < 2) return null;

  const total = counts ? Object.values(counts).reduce((sum, n) => sum + n, 0) : null;
  const options: { key: string; label: string; count: number | null }[] = [
    { key: "", label: "all", count: total },
    ...known.map((name) => ({
      key: name,
      label: shortPipeline(name),
      count: counts ? (counts[name] ?? 0) : null,
    })),
  ];

  return (
    <div
      data-testid={testId}
      role="group"
      aria-label="authoring pipeline"
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <span style={{ fontSize: 11.5, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
        {label}
      </span>
      <div
        style={{
          display: "inline-flex",
          borderRadius: 8,
          border: "1px solid var(--color-border)",
          overflow: "hidden",
        }}
      >
        {options.map((option) => {
          const active = option.key === value;
          return (
            <button
              key={option.key || "all"}
              type="button"
              data-testid={`${testId}-${option.key || "all"}`}
              aria-pressed={active}
              title={option.key ? `only vessels forged by ${option.key}` : "every pipeline"}
              onClick={() => onChange(option.key)}
              style={{
                minHeight: 28,
                padding: "3px 10px",
                border: "none",
                borderLeft: option.key ? "1px solid var(--color-border)" : "none",
                background: active ? "var(--color-primary-muted)" : "var(--color-bg-subtle)",
                color: active ? "var(--color-text)" : "var(--color-text-muted)",
                font: "inherit",
                fontSize: 12.5,
                fontWeight: active ? 600 : 400,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {option.label}
              {option.count === null ? null : (
                <span style={{ marginLeft: 6, opacity: 0.65, fontVariantNumeric: "tabular-nums" }}>
                  {option.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
